import { Grid } from '@material-ui/core';
import TextInput from 'app/components/Form/Inputs/TextInput';
import RichText from 'app/components/RichText';
import { useOnClickOutside } from 'app/hooks/use-outside-click';
import { appDir } from 'app/utils/check_lang';
import { _t } from 'app/utils/i18n';
import { removeHTMLTags } from 'app/utils/stringMethods';
import React, { FunctionComponent, memo, useRef, useState } from 'react';

import styles from './ReportTypography.module.scss';

interface Props {
  id?: number;
  module_slug_id?: number;
  paragraph?: string;
  name?: string;
  slug: string;
  name_ar?: string;
  order?: number;
  chart_type?: string;
  item_count?: number;
  item_sort_type_id?: number;
  description?: string;
  moduleName?: string;
  itemsName?: any;
  title?: string;
  changeHandler?: (key: string, value: any) => void;
  values: any;
  isStatic?: boolean;
  setError?: any;
  errors?: any;
  clearErrors?: any;
}

const ReportTypography: FunctionComponent<Props> = ({
  slug,
  moduleName,
  paragraph,
  itemsName,
  title,
  description,
  changeHandler,
  values,
  id,
  isStatic,
  setError,
  errors,
  clearErrors,
}) => {
  const [localValue, setLocalValue] = useState(
    values?.charts?.filter((i: any) => i.id === id)?.[0]?.paragraph
  );
  const ref = useRef<any>();
  const renderName = () => {
    const newName =
      moduleName === 'HASHTAGS'
        ? `#${itemsName?.replaceAll(/,/g, ', #')}`
        : itemsName;
    return newName;
  };

  const keyName = `${slug}_${id}`;
  const slugs = ['sub_title', 'title'];
  const handleLocalChange = (
    name = slug === 'sub_title'
      ? 'report_sub_title'
      : slug === 'title'
      ? 'report_title'
      : 'paragraph'
  ) => {
    changeHandler?.(
      'charts',
      values?.charts?.map((item: any) =>
        item.id === id ? { ...item, paragraph: localValue } : item
      )
    );
    const isThereImage = !localValue.includes('<img');
    if (removeHTMLTags(localValue)?.length === 0 && isThereImage) {
      setError(keyName, {
        message: _t('basic:err_required', {
          fieldName: _t(`reports:${name}`),
        }),
      });
      return;
    }
    if (slugs.includes(slug) && localValue?.length > 500) {
      setError(keyName, {
        message: _t('basic:err_max', {
          fieldName: _t(`reports:${name}`),
          length: 500,
        }),
      });
      return;
    }

    if (
      localValue?.replaceAll(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, '')
        ?.length >= 2 ||
      !isThereImage
    ) {
      clearErrors(keyName);
    } else {
      setError(keyName, {
        message: _t('basic:err_min', {
          fieldName: _t(`reports:${name}`),
          length: 2,
        }),
      });
    }
  };
  useOnClickOutside(ref, handleLocalChange);

  const RenderText = () => {
    switch (slug) {
      case 'report_name':
        return (
          <Grid item sm={12}>
            <div className={styles.ReportTypography}>
              <div className={styles.TitleBox}>
                {!isStatic && <h3>{_t('reports:report_name')}</h3>}
                <h4 dir={appDir()}>
                  {Array.isArray(itemsName)
                    ? itemsName.join('  V.S.  ')
                    : renderName()}
                </h4>
              </div>
            </div>
          </Grid>
        );

      case 'report_summary':
        return (
          <>
            {!!paragraph && (
              <Grid item sm={12}>
                <div className={styles.ReportTypography}>
                  <div className={styles.TitleBox}>
                    <h3>{_t('reports:report_summary')}</h3>
                    <div
                      dir={appDir()}
                      className={styles.paragraph}
                      dangerouslySetInnerHTML={{ __html: paragraph }}
                    />
                  </div>
                </div>
              </Grid>
            )}
          </>
        );

      case 'sections':
        return (
          <>
            {!!title && (
              <Grid item sm={12}>
                <div className={styles.ReportTypography}>
                  <div className={styles.TitleBox}>
                    <h3>{_t('reports:section_title')}</h3>
                    <h4 dir={appDir()}>{title}</h4>
                  </div>
                </div>
              </Grid>
            )}
            {!!description && (
              <Grid item sm={12}>
                <div className={styles.ReportTypography}>
                  <div className={styles.TitleBox}>
                    <h3>{_t('reports:section_description')}</h3>
                    <h4 dir={appDir()}>{description}</h4>
                  </div>
                </div>
              </Grid>
            )}
          </>
        );
      case 'title':
        return (
          <Grid item sm={12}>
            <div className={styles.ReportTypography}>
              <div className={styles.TitleBox} ref={ref}>
                <h3>{_t('reports:report_title')}</h3>

                <TextInput
                  value={localValue}
                  error={errors?.[keyName]?.message}
                  name="report_title"
                  onChange={(value) => {
                    setLocalValue(value);
                  }}
                  onBlur={() => {
                    handleLocalChange('report_title');
                  }}
                />
              </div>
            </div>
          </Grid>
        );

      case 'sub_title':
        return (
          <Grid item sm={12}>
            <div className={styles.ReportTypography}>
              <div className={styles.TitleBox} ref={ref}>
                <h3>{_t('reports:report_sub_title')}</h3>
                <TextInput
                  value={localValue}
                  error={errors?.[keyName]?.message}
                  onChange={(value) => {
                    setLocalValue(value);
                  }}
                  name="report_sub_title"
                  onBlur={() => {
                    handleLocalChange('report_sub_title');
                  }}
                />
              </div>
            </div>
          </Grid>
        );

      case 'paragraph':
        return (
          <Grid item sm={12}>
            <div className={styles.ReportTypography}>
              <div
                className={styles.TitleBox}
                ref={ref}
                onMouseLeave={() => handleLocalChange('paragraph')}
              >
                <RichText
                  name={keyName}
                  error={errors}
                  value={localValue}
                  onChange={(value) => {
                    setLocalValue(value);
                  }}
                  onBlur={() => {
                    handleLocalChange('paragraph');
                  }}
                />
              </div>
            </div>
          </Grid>
        );

      default:
        return <></>;
    }
  };

  return RenderText();
};
const MemoizedReportTypography = memo(ReportTypography);
export default MemoizedReportTypography;
